import React, { useState } from "react";

export interface LastMonthRevenueCardProps {
  monthLabel?: string;
  statusLabel?: string;
  statusVariant?: "healthy" | "watch" | "critical";
  revenueAmount?: string;
  momPercent?: string;
  ebitdaPercent?: string;
  /** Bar heights for mini chart (e.g. [40, 60, 55, 50, 80] for 5 months); last is current month */
  barHeights?: number[];
  barLabels?: string[];
  /** Formatted revenue amount per bar for tooltip (e.g. ["₹10L", "₹12L", ...]) */
  barAmounts?: string[];
  loading?: boolean;
}

function LastMonthRevenueCardSkeleton() {
  return (
    <div
      className="flex flex-col items-start justify-between rounded-lg bg-[#e6f4ea] px-6 py-6 flex-1 min-w-0 animate-pulse"
      aria-hidden
    >
      {/* Top row: label + status badge */}
      <div className="flex w-full items-start justify-between">
        <div className="flex flex-col items-start gap-2">
          <div className="h-3.5 w-16 rounded bg-[#4a6b5a]/20" />
          <div className="h-5 w-32 rounded bg-[#4a6b5a]/20" />
        </div>
        <div className="h-7 w-16 rounded-full bg-[#4a6b5a]/20" />
      </div>

      {/* Middle row: revenue + bar chart */}
      <div className="flex w-full items-end justify-between gap-4">
        <div className="flex flex-col items-start gap-2">
          <div className="h-10 w-28 rounded bg-[#1a3a2a]/20" />
          <div className="h-4 w-14 rounded bg-[#4a6b5a]/20" />
        </div>
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-end gap-2">
            <div className="h-12 w-6 rounded-sm bg-[#4a6b5a]/20" />
            <div className="h-20 w-6 rounded-sm bg-[#4a6b5a]/20" />
            <div className="h-16 w-6 rounded-sm bg-[#4a6b5a]/20" />
            <div className="h-14 w-6 rounded-sm bg-[#4a6b5a]/20" />
            <div className="h-24 w-6 rounded-sm bg-[#4a6b5a]/30 pt-4" />
          </div>
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-3 w-5 rounded bg-[#4a6b5a]/20" />
            ))}
          </div>
        </div>
      </div>

      {/* Bottom row: MoM + EBITDA pills */}
      <div className="flex items-center gap-3">
        <div className="h-7 w-20 rounded-full bg-[#4a6b5a]/20" />
        <div className="h-7 w-24 rounded-full bg-[#4a6b5a]/20" />
      </div>
    </div>
  );
}

export function LastMonthRevenueCard({
  monthLabel = "January 2025 · Closed",
  statusLabel = "Strong",
  statusVariant = "healthy",
  revenueAmount = "—",
  momPercent = "+22% MoM",
  ebitdaPercent = "22.5% EBITDA",
  barHeights = [40, 64, 56, 48, 80],
  barLabels = ["Sep", "Oct", "Nov", "Dec", "Jan"],
  barAmounts,
  loading = false,
}: LastMonthRevenueCardProps) {
  const [hoveredBarIndex, setHoveredBarIndex] = useState<number | null>(null);

  if (loading) {
    return <LastMonthRevenueCardSkeleton />;
  }

  const maxH = barHeights.length > 0 ? Math.max(...barHeights, 1) : 1;

  const statusStyles =
    statusVariant === "healthy"
      ? {
          dot: "bg-success-500",
          text: "text-success-700",
          cardBg: "bg-[#e6f4ea]",
          barFrom: "from-[#bbf7d0]",
          barTo: "to-[#86efac]",
          line: "border-success-400",
          lastDotBorder: "border-success-600",
        }
      : statusVariant === "watch"
        ? {
            dot: "bg-warning-500",
            text: "text-warning-700",
            cardBg: "bg-[#fff7ed]",
            barFrom: "from-[#fed7aa]",
            barTo: "to-[#fdba74]",
            line: "border-warning-400",
            lastDotBorder: "border-warning-600",
          }
        : {
            dot: "bg-error-500",
            text: "text-error-700",
            cardBg: "bg-[#fef2f2]",
            barFrom: "from-[#fecaca]",
            barTo: "to-[#fca5a5]",
            line: "border-error-400",
            lastDotBorder: "border-error-600",
          };

  return (
    <div
      className={`flex flex-col items-start justify-between rounded-lg px-6 py-6 flex-1 min-w-0 ${statusStyles.cardBg}`}
    >
      <div className="flex w-full items-start justify-between">
        <div className="flex flex-col items-start gap-1">
          <span
            className={`text-caption-bold font-caption-bold uppercase tracking-wider ${statusStyles.text}`}
          >
            Last Month
          </span>
          <span className={`text-body font-body ${statusStyles.text}`}>
            {monthLabel}
          </span>
        </div>
        {/* <div className="flex items-center gap-2 rounded-full border border-solid border-[#d1e9d6] bg-white px-3 py-1">
          <div
            className={`flex h-2 w-2 flex-none flex-col items-start rounded-full ${statusStyles.dot}`}
          />
          <span
            className={`text-body-bold font-body-bold ${statusStyles.text}`}
          >
            {statusLabel}
          </span>
        </div> */}
      </div>
      <div className="flex w-full items-end justify-between pb-4">
        <div className="flex flex-col items-start gap-1">
          <span className="font-['Inter'] text-[40px] font-[700] leading-[40px] text-[#1a3a2a]">
            {revenueAmount}
          </span>
          <span className="text-body font-body text-[#4a6b5a]">Revenue</span>
        </div>
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-end gap-2 relative">
            <div className="flex grow shrink-0 basis-0 items-center gap-1 absolute top-[40%] left-0">
              <div
                className={`flex items-start border-t-2 border-dashed flex-1 ${statusStyles.line}`}
              />
            </div>
            {barHeights.map((h, i) => {
              const isLast = i === barHeights.length - 1;
              const label = barLabels[i] ?? "";
              const amount = barAmounts?.[i];
              const isHovered = hoveredBarIndex === i;
              return (
                <div
                  key={i}
                  className="relative flex w-6 flex-none flex-col items-center rounded-sm cursor-default"
                  onMouseEnter={() => setHoveredBarIndex(i)}
                  onMouseLeave={() => setHoveredBarIndex(null)}
                >
                  {isHovered && (amount != null || label) && (
                    <div
                      className="absolute bottom-full left-1/2 z-10 mb-1 -translate-x-1/2 whitespace-nowrap rounded-md border border-[#d1e9d6] bg-white px-2 py-1.5 shadow-md"
                      role="tooltip"
                    >
                      <span className="text-caption-bold font-caption-bold text-[#1a3a2a]">
                        {label}
                        {amount != null && ` · ${amount}`}
                      </span>
                    </div>
                  )}
                  <div
                    className={`flex w-full flex-col items-center rounded-sm transition-opacity ${
                      isLast
                        ? `pt-4 bg-gradient-to-b ${statusStyles.barFrom} ${statusStyles.barTo} relative`
                        : `bg-gradient-to-b ${statusStyles.barFrom} ${statusStyles.barTo}`
                    } ${isHovered ? "opacity-90" : ""}`}
                    style={{
                      height: Math.max(12, (h / maxH) * 80),
                    }}
                  >
                    {isLast && (
                      <div
                        className={`flex h-3 w-3 flex-none flex-col items-center rounded-full border-2 border-solid bg-white ${statusStyles.lastDotBorder}`}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            {barLabels.map((l, i) => (
              <span
                key={i}
                className={`text-caption font-caption text-[#4a6b5a] ${i === barLabels.length - 1 ? "text-caption-bold font-caption-bold" : ""}`}
              >
                {l}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex items-center rounded-full border border-solid border-[#d1e9d6] bg-white px-3 py-1">
          <span
            className={`text-body-bold font-body-bold ${statusStyles.text}`}
          >
            {momPercent}
          </span>
        </div>
        <div className="flex items-center rounded-full border border-solid border-[#d1e9d6] bg-white px-3 py-1">
          <span className="text-body-bold font-body-bold text-neutral-700">
            {ebitdaPercent}
          </span>
        </div>
      </div>
    </div>
  );
}
